import AuthService from '../services/AuthService';

// Use a safer way to access environment variables that works in all environments
const getApiBaseUrl = (): string => {
  try {
    // If REACT_APP_API_URL is explicitly set, use it (for development)
    if (process?.env?.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL;
    }

    // In production (when served from chelseachorus.com), use the same origin
    if (window.location.origin && window.location.origin.includes('chelseachorus.com')) {
      return window.location.origin;
    }

    // For local development, default to localhost
    return 'http://localhost:8080';
  } catch {
    // If we can't access window or process, check if we're on the production domain
    if (typeof window !== 'undefined' && window.location.origin && window.location.origin.includes('chelseachorus.com')) {
      return window.location.origin;
    }
    // Default fallback for development
    return 'http://localhost:8080';
  }
};

const API_BASE_URL = getApiBaseUrl();

export const apiConfig = {
  baseURL: API_BASE_URL,
  timeout: 10000,
};

// Custom API fetcher with JWT authentication
export const apiFetch = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  // Prepare URL
  const url = `${apiConfig.baseURL}${endpoint}`;

  // Get token from user object in localStorage
  let token = null;
  let userData = null;
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      userData = JSON.parse(storedUser);
      token = userData.token;

      // Add debugging for JWT token
      if (url.includes('/api/admin/choirs') || url.includes('/api/admin/headlines')) {
        console.log('=== Frontend JWT Debug ===');
        console.log('Request URL:', url);
        console.log('User data from localStorage:', userData);
        console.log('Token present:', !!token);
        if (token) {
          // Decode JWT payload for debugging (without verification)
          try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
              return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            const payload = JSON.parse(jsonPayload);
            console.log('JWT payload:', payload);
          } catch (e) {
            console.log('Failed to decode JWT payload:', e);
          }
        }
        console.log('=== End Frontend JWT Debug ===');
      }
    }
  } catch (err) {
    console.error('Error parsing user data from localStorage:', err);
  }

  // Prepare headers with authentication if available
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...(options.headers || {})
  };

  // Merge options
  const requestOptions = {
    ...options,
    headers
  };

  try {
    const response = await fetch(url, requestOptions);

    // Handle authentication errors (both 401 and 403 can indicate token issues)
    if (response.status === 401 || response.status === 403) {
      // Check if this is a JWT signature validation error
      const errorText = await response.text();
      let isTokenError = false;

      try {
        const errorData = JSON.parse(errorText);
        // More precise detection of JWT/authentication errors vs authorization errors
        isTokenError = errorData.message?.includes('JWT') ||
                      errorData.message?.includes('signature') ||
                      errorData.message?.includes('expired') ||
                      errorData.message?.includes('invalid token') ||
                      errorData.error?.includes('JWT') ||
                      errorData.error?.includes('signature') ||
                      response.status === 401; // 401 is always an auth issue

        // Don't treat 403 as auth error unless it specifically mentions JWT/token issues
        // 403 is often just insufficient permissions with a valid token
      } catch {
        // If we can't parse the error, only assume token issue for 401
        isTokenError = response.status === 401;
      }

      if (isTokenError) {
        console.warn('Invalid or expired JWT token detected, forcing logout');
        AuthService.logout();

        // Use forceLogout if available globally, otherwise fallback to redirect
        if (typeof window !== 'undefined' && (window as any).forceLogout) {
          (window as any).forceLogout();
          return; // forceLogout will reload the page
        } else {
          window.location.href = '/login';
        }

        throw new Error('Authentication session expired. Please login again.');
      }

      // If not a token error, re-parse the response for the actual error
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.message || `Access denied: ${response.status}`);
      } catch {
        throw new Error(`Access denied: ${response.status}`);
      }
    }

    // For successful GET requests with no content
    if (response.status === 204) {
      return null;
    }

    // Parse JSON for non-204 responses
    const data = await response.json();

    // Handle server errors
    if (!response.ok) {
      throw new Error(data.message || `API Error: ${response.status}`);
    }

    return data;
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// Convenience methods for common HTTP methods
export const apiClient = {
  get: (endpoint: string, options?: RequestInit) =>
    apiFetch(endpoint, { ...options, method: 'GET' }),

  post: (endpoint: string, data?: any, options?: RequestInit) =>
    apiFetch(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    }),

  put: (endpoint: string, data?: any, options?: RequestInit) =>
    apiFetch(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    }),

  patch: (endpoint: string, data?: any, options?: RequestInit) =>
    apiFetch(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined
    }),

  delete: (endpoint: string, options?: RequestInit) =>
    apiFetch(endpoint, { ...options, method: 'DELETE' }),
};
